import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { ListParams } from '@/components/data/data-table';

import {
  createDiscount,
  deleteDiscount,
  getApplicableEntities,
  getDiscount,
  listMetaBundles,
  listMetaCustomers,
  listMetaDevices,
  setDiscountStatus,
} from './api';
import type {
  ApplicableEntityType,
  CreateDiscountBody,
  DeviceMetaOption,
  DeviceRef,
  MetaOption,
  MetaParams,
} from './types';

/** Applicable-entities list params (page/limit/sort + optional name search) for the detail
 *  relationship tabs (ticket 06). */
type EntitiesParams = ListParams<{ search?: string }>;

/** Query-key factory for the discounts feature. `all`/`lists()` align with the DataTable
 *  `tableId='discounts'` so status/delete mutations (tickets 04/07) invalidate the listing;
 *  the `meta*` keys back the AsyncMultiSelect pickers (ticket 08). */
export const discountKeys = {
  all: ['discounts'] as const,
  metaCustomers: (search: string) => [...discountKeys.all, 'meta', 'customers', search] as const,
  metaBundles: (search: string) => [...discountKeys.all, 'meta', 'bundles', search] as const,
  metaDevices: (search: string) => [...discountKeys.all, 'meta', 'devices', search] as const,
  lists: () => [...discountKeys.all, 'list'] as const,
  detail: (id: string) => [...discountKeys.all, 'detail', id] as const,
  entities: (id: string, type: ApplicableEntityType, params: EntitiesParams) =>
    [...discountKeys.detail(id), 'entities', type, params] as const,
};

/** Create a discount, then invalidate the discount list so it refreshes on return to the listing. */
export function useCreateDiscount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateDiscountBody) => createDiscount(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: discountKeys.all }),
  });
}

const cursorPage =
  <T>(
    fetch: (
      p: MetaParams,
    ) => Promise<{ data: T[]; meta: { nextCursor?: string; hasNextPage: boolean } }>,
  ) =>
  async (p: MetaParams) => {
    const page = await fetch(p);
    return {
      data: page.data,
      nextCursor: page.meta.nextCursor,
      hasNextPage: page.meta.hasNextPage,
    };
  };

/** A source config = everything `AsyncMultiSelect` feeds to `useCursorSelect` + how to shape the value. */
export const customerMetaSource = {
  queryKey: discountKeys.metaCustomers,
  fetchPage: cursorPage(listMetaCustomers),
  toOption: (c: MetaOption) => ({ value: c.id, label: c.name }),
  toItem: (c: MetaOption): string => c.id,
  itemId: (id: string) => id,
};

export const bundleMetaSource = {
  queryKey: discountKeys.metaBundles,
  fetchPage: cursorPage(listMetaBundles),
  toOption: (b: MetaOption) => ({ value: b.id, label: b.name }),
  toItem: (b: MetaOption): string => b.id,
  itemId: (id: string) => id,
};

export const deviceMetaSource = {
  queryKey: discountKeys.metaDevices,
  fetchPage: cursorPage(listMetaDevices),
  toOption: (d: DeviceMetaOption) => ({ value: d.id, label: d.name }),
  toItem: (d: DeviceMetaOption): DeviceRef => ({ id: d.id, isComponent: d.isComponent }),
  itemId: (ref: DeviceRef) => ref.id,
};

/** PATCH /discounts/admin/:id/status. Invalidates the `['discounts']` prefix (shared by the
 *  DataTable tableId='discounts' query) so the listing refetches and the row chip flips. */
export function useSetDiscountStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: 'active' | 'inactive' }) =>
      setDiscountStatus(id, status),
    onSuccess: () => void qc.invalidateQueries({ queryKey: discountKeys.all }),
  });
}

/** DELETE /discounts/admin/:id. Invalidates the listing so the deleted row leaves the list. */
export function useDeleteDiscount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteDiscount(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: discountKeys.all }),
  });
}

/** Detail query keyed by `discountKeys.detail(id)`; disabled on an empty id. */
export function useDiscount(id: string) {
  return useQuery({
    queryKey: discountKeys.detail(id),
    queryFn: () => getDiscount(id),
    enabled: Boolean(id),
  });
}

/** One page of a discount's applicable entities for the relationship tabs. `keepPreviousData`
 *  keeps the prior page visible while paging/searching; disabled on an empty id. */
export function useApplicableEntities<T extends ApplicableEntityType>(
  id: string,
  type: T,
  params: EntitiesParams,
) {
  return useQuery({
    queryKey: discountKeys.entities(id, type, params),
    queryFn: () => getApplicableEntities(id, type, params),
    enabled: Boolean(id),
    placeholderData: keepPreviousData,
  });
}
